import React, { useEffect } from "react";
import { Box, Typography, List, ListItem, ListItemText } from "@mui/material";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useLocation } from "react-router-dom";
import { bodyTextSx } from "../Pages/ContentPageLayout";
import { NAV } from "../Navbar/navbarTheme";
import heroImg from "../Images/OurService/Our Services.png";
import productSourcingImg from "../Images/OurService/Product Sourcing & Wholesale Supply.png";
import consolidationImg from "../Images/OurService/Consolidation & Loading Solutions.png";
import transportImg from "../Images/OurService/Transportation & Logistics Support.jpg";
import labellingImg from "../Images/OurService/Labelling & Packaging Services.png";
import exportDocsImg from "../Images/OurService/Export Documentation Support.jpg";

const FEATURE_STRIP_BG = "#002147";

const serviceFeatures = [
  {
    title: "Reliable Supply",
    subtitle: "Consistent stock and trusted partnerships",
    Icon: HandshakeOutlinedIcon,
  },
  {
    title: "Competitive Pricing",
    subtitle: "Best value through strong supplier relationships",
    Icon: SellOutlinedIcon,
  },
  {
    title: "Flexible Ordering",
    subtitle: "From single cases to full container loads",
    Icon: Inventory2OutlinedIcon,
  },
  {
    title: "Global Markets",
    subtitle: "Serving UK businesses and international clients",
    Icon: PublicOutlinedIcon,
  },
  {
    title: "Responsive Support",
    subtitle: "Dedicated team providing quick and reliable service",
    Icon: SupportAgentOutlinedIcon,
  },
  {
    title: "Quality Assured",
    subtitle: "Carefully sourced FMCG products you can trust",
    Icon: ShieldOutlinedIcon,
  },
];

const introParagraphs = [
  "At UK TO MARS LTD, we provide flexible wholesale and export solutions designed to support retailers, wholesalers, distributors, supermarkets, and international buyers across global markets.",
  "From sourcing British and European FMCG brands to managing consolidation, logistics, and export documentation, our goal is to simplify the supply process and help customers access quality products efficiently and competitively.",
  "Whether you require individual cases, mixed pallets, full truckloads, or container shipments, our team works closely with customers to build wholesale solutions backed by reliable service and strong supplier relationships.",
];

const serviceSections = [
  {
    title: "Product Sourcing & Wholesale Supply",
    image: productSourcingImg,
    imageAlt: "Product sourcing and wholesale supply",
    paragraphs: [
      "We supply a wide range of British and selected European FMCG products across food, drinks, confectionery, grocery, household, personal care, baby care, frozen, pet care, and specialist categories, including seasonal and promotional stock lines.",
      "With access to a growing supplier network across the UK and Europe, we can also source many products requested by customers, including items not currently listed on our website.",
      "Our supply solutions include:",
    ],
    bullets: [
      "Individual case quantities",
      "Layer pallets",
      "Mixed pallets",
      "Full pallets",
      "Full truckloads",
      "Container shipments",
    ],
    closing: [
      "Minimum order value requirements apply for wholesale and export orders.",
    ],
  },
  {
    title: "Consolidation & Loading Solutions",
    image: consolidationImg,
    imageAlt: "Consolidation and loading solutions",
    paragraphs: [
      "Managing products from multiple suppliers can be time-consuming and costly. Our consolidation service simplifies the process by combining products into organised shipments ready for export or local delivery.",
      "Our team carefully prepares and consolidates orders to maximise space efficiency while helping reduce transport costs and unnecessary handling.",
      "Services include:",
    ],
    bullets: [
      "Mixed product consolidation",
      "Palletised loading",
      "Manual hand-loading when required",
      "Optimised container utilisation",
      "Dry and temperature-sensitive shipment coordination",
      "Secure packing and handling",
    ],
  },
  {
    title: "Transportation & Logistics Support",
    image: transportImg,
    imageAlt: "Transportation and logistics support",
    paragraphs: [
      "We work with established logistics and transport partners to arrange reliable shipping solutions tailored to customer requirements.",
      "Logistics support includes:",
    ],
    bullets: [
      "Road freight",
      "Sea freight",
      "Air freight",
      "Export shipment coordination",
      "Flexible delivery solutions",
      "Temperature-controlled transport support",
      "Competitive freight rates",
      "Live shipment tracking support",
    ],
  },
  {
    title: "Labelling & Packaging Services",
    image: labellingImg,
    imageAlt: "Labelling and packaging services",
    paragraphs: [
      "We support customers with product labelling and packaging requirements to help products meet destination market needs and compliance standards.",
      "Services include:",
    ],
    bullets: [
      "Ingredient translation support",
      "Multi-language labelling",
      "Market-specific label requirements",
      "Packaging coordination",
    ],
  },
  {
    title: "Export Documentation Support",
    image: exportDocsImg,
    imageAlt: "Export documentation support",
    paragraphs: [
      "We assist in preparing and coordinating required export documentation to support smooth customs clearance and international shipment processing.",
      "Documentation support includes:",
    ],
    bullets: [
      "Commercial Invoices",
      "Packing Lists",
      "Certificates of Origin",
      "Bills of Lading",
      "Health Certificates",
      "Certificates of Free Sale",
      "EUR1 Movement Certificates",
      "ELO (Enveloppe Logistique Obligatoire)",
      "ENS (Entry Summary Declaration)",
      "T1 Transit/Security Accompanying Documents",
    ],
  },
];

const closingParagraphs = [
  "We are committed to building long-term partnerships through reliable supply, competitive pricing, flexible ordering, and responsive customer support.",
  "Whether supplying UK businesses or supporting international exports, UK TO MARS LTD focuses on delivering efficient wholesale solutions tailored to modern FMCG trade requirements.",
];

const pageContentSx = {
  px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
  py: { xs: 3.5, md: 5 },
  maxWidth: 1240,
  mx: "auto",
  width: "100%",
  boxSizing: "border-box",
};

const listItemSx = {
  padding: 0,
  display: "list-item",
  pl: 2.5,
  mb: 0.5,
};

const bulletListSx = {
  color: NAV.ink,
  mb: 0,
  pl: { xs: 2, sm: 2.5 },
  listStyleType: "disc",
};

const serviceHeadingSx = {
  color: NAV.ink,
  fontSize: { xs: "1.25rem", md: "1.5rem" },
  fontWeight: 700,
  lineHeight: 1.3,
  mb: { xs: 1.75, md: 2 },
};

const serviceBodySx = {
  color: NAV.ink,
  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
  lineHeight: 1.7,
  mb: 1.5,
};

function ServiceBulletList({ items }) {
  if (!items?.length) return null;
  return (
    <List dense sx={bulletListSx}>
      {items.map((item) => (
        <ListItem key={item} sx={listItemSx}>
          <ListItemText
            primary={item}
            primaryTypographyProps={{
              sx: {
                fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                lineHeight: 1.7,
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function ServiceFeatureItem({ title, subtitle, Icon }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: { xs: 44, md: 48 },
          height: { xs: 44, md: 48 },
          borderRadius: "50%",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: FEATURE_STRIP_BG, fontSize: { xs: 22, md: 24 } }} />
      </Box>
      <Box sx={{ minWidth: 0, pt: 0.25 }}>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.88)",
            fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            lineHeight: 1.45,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

function ServiceFeatureStrip() {
  return (
    <Box
      component="section"
      aria-label="Our service strengths"
      sx={{
        width: "100%",
        bgcolor: FEATURE_STRIP_BG,
        py: { xs: 3, md: 4 },
        px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(6, 1fr)",
          },
          gap: { xs: 2.5, md: 3, lg: 2.5 },
          alignItems: "start",
        }}
      >
        {serviceFeatures.map(({ title, subtitle, Icon }) => (
          <ServiceFeatureItem
            key={title}
            title={title}
            subtitle={subtitle}
            Icon={Icon}
          />
        ))}
      </Box>
    </Box>
  );
}

function ServiceSection({ section, index, imageLeft }) {
  const { title, image, imageAlt, paragraphs, bullets, closing = [] } = section;

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          md: imageLeft ? "row-reverse" : "row",
        },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: { xs: 3, md: 5, lg: 6 },
        py: { xs: 3.5, md: 4.5, lg: 5 },
        bgcolor: "#fff",
        borderTop: index > 0 ? `1px solid ${NAV.line}` : "none",
      }}
    >
      <Box
        sx={{
          flex: { md: "1 1 42%" },
          minWidth: 0,
          pr: { md: imageLeft ? 0 : 1, lg: 2 },
          pl: { md: imageLeft ? 1 : 0, lg: imageLeft ? 2 : 0 },
        }}
      >
        <Typography component="h2" sx={serviceHeadingSx}>
          {title}
        </Typography>
        {paragraphs.map((text) => (
          <Typography
            key={text}
            sx={{
              ...serviceBodySx,
              fontWeight: text.endsWith(":") ? 600 : 400,
            }}
          >
            {text}
          </Typography>
        ))}
        <ServiceBulletList items={bullets} />
        {closing.map((text) => (
          <Typography
            key={text}
            sx={{
              ...serviceBodySx,
              mt: 1.5,
              mb: 0,
              fontStyle: "italic",
              color: NAV.inkMuted,
            }}
          >
            {text}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          flex: { md: "1 1 58%" },
          minWidth: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            maxWidth: { xs: 480, sm: 560, md: "100%", lg: "100%" },
            maxHeight: { md: 620, lg: 700 },
            mx: { xs: "auto", md: 0 },
            objectFit: "contain",
            objectPosition: "center center",
          }}
        />
      </Box>
    </Box>
  );
}

const OurService = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box component="main" sx={{ width: "100%" }}>
      <Box
        component="header"
        sx={{
          width: "100%",
          bgcolor: "#eef5fb",
          borderBottom: `1px solid ${NAV.line}`,
        }}
      >
        <Box
          component="img"
          src={heroImg}
          alt="Our Services — Reliable FMCG Wholesale & Export Solutions"
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            objectFit: "contain",
            objectPosition: "center top",
          }}
        />
      </Box>

      <Box sx={pageContentSx}>
        <Box
          component="section"
          aria-label="Introduction"
          sx={{
            mb: { xs: 3, md: 4 },
            pb: { xs: 3, md: 4 },
            borderBottom: `1px solid ${NAV.line}`,
          }}
        >
          {introParagraphs.map((text, index) => (
            <Typography
              key={text}
              sx={{
                ...serviceBodySx,
                mb: index < introParagraphs.length - 1 ? 2 : 0,
                maxWidth: 900,
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>

        {serviceSections.map((section, index) => (
          <ServiceSection
            key={section.title}
            section={section}
            index={index}
            imageLeft={index % 2 === 1}
          />
        ))}

        <Box
          component="section"
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            bgcolor: NAV.surfaceSubtle,
            border: `1px solid ${NAV.line}`,
            mt: { xs: 1, md: 2 },
          }}
        >
          <Typography component="h2" sx={serviceHeadingSx}>
            Flexible Wholesale Solutions for Global Markets
          </Typography>
          {closingParagraphs.map((text, index) => (
            <Typography
              key={text}
              sx={{
                ...bodyTextSx,
                mb: index < closingParagraphs.length - 1 ? 2 : 0,
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      </Box>

      <ServiceFeatureStrip />
    </Box>
  );
};

export default OurService;
